/**
 * 拖拽系统 Hook
 * 支持鼠标和触摸操作，位置持久化
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import { POSITION_STORAGE_KEY } from './types'

interface Position {
  x: number
  y: number
}

interface UseDraggableOptions {
  elementSize: number
  onDragStart?: () => void
  onDragEnd?: () => void
}

// 获取默认位置（右下角，距边缘24px）
function getDefaultPosition(elementSize: number): Position {
  return {
    x: window.innerWidth - elementSize - 24,
    y: window.innerHeight - elementSize - 24,
  }
}

// 约束位置不超出视口
function clampPosition(pos: Position, elementSize: number): Position {
  return {
    x: Math.max(0, Math.min(pos.x, window.innerWidth - elementSize)),
    y: Math.max(0, Math.min(pos.y, window.innerHeight - elementSize)),
  }
}

// 从 localStorage 读取位置
function loadPosition(elementSize: number): Position {
  try {
    const saved = localStorage.getItem(POSITION_STORAGE_KEY)
    if (saved) {
      const pos = JSON.parse(saved) as Position
      return clampPosition(pos, elementSize)
    }
  } catch {
    // 静默失败
  }
  return getDefaultPosition(elementSize)
}

// 保存位置到 localStorage
function savePosition(pos: Position) {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos))
  } catch {
    // 静默失败
  }
}

export function useDraggable({ elementSize, onDragStart, onDragEnd }: UseDraggableOptions) {
  const [position, setPosition] = useState<Position>(() => loadPosition(elementSize))
  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef<HTMLDivElement>(null)
  const offsetRef = useRef<Position>({ x: 0, y: 0 })
  const hasDraggedRef = useRef(false)

  // 鼠标/触摸按下
  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault()
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

      offsetRef.current = {
        x: clientX - position.x,
        y: clientY - position.y,
      }
      hasDraggedRef.current = false
      setIsDragging(true)
      onDragStart?.()
    },
    [position, onDragStart]
  )

  // 全局移动事件处理
  useEffect(() => {
    if (!isDragging) return

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

      const newPos = clampPosition(
        {
          x: clientX - offsetRef.current.x,
          y: clientY - offsetRef.current.y,
        },
        elementSize
      )

      hasDraggedRef.current = true
      setPosition(newPos)
    }

    const handleUp = () => {
      setIsDragging(false)
      onDragEnd?.()
      setPosition((pos) => {
        savePosition(pos)
        return pos
      })
    }

    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('touchend', handleUp)

    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleUp)
    }
  }, [isDragging, elementSize, onDragEnd])

  // 窗口 resize 时约束位置
  useEffect(() => {
    const handleResize = () => {
      setPosition((pos) => clampPosition(pos, elementSize))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [elementSize])

  return {
    position,
    isDragging,
    hasDragged: hasDraggedRef,
    dragRef,
    handlePointerDown,
  }
}
